import React, { useImperativeHandle, forwardRef } from "react";
import "../../style/common/payment.scss";
import Visa from "../../icon/Visa";
import MasterCard from "../../icon/MasterCard";
import Discover from "../../icon/Discover";
import American from "../../icon/American";
import JCB from "../../icon/JCB";

const Payment = forwardRef((props, ref) => {
  let [cardNumber, setCardNumber] = React.useState("");
  let [cvc, setCvc] = React.useState("");
  let [expiry, setExpiry] = React.useState("");
  let [cardValid, setCardValid] = React.useState(false);
  let [cvcValid, setCvcValid] = React.useState(false);
  let [expiryValid, setExpiryValid] = React.useState(false);
  let [cardType, setCardType] = React.useState("");
  let [errors, setErrors] = React.useState({});

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    getValid,
    getPaymentVaild,
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "cardNumber") {
      processedValue = value.replace(/\D/g, "").slice(0, 16);
      processedValue = processedValue.replace(/(.{4})/g, "$1 ").trim();
      setCardNumber(processedValue);
      detectCardType(processedValue.replace(/\s/g, ""));
    } else if (name === "expiry") {
      processedValue = value.replace(/\D/g, "").slice(0, 4);
      if (processedValue.length >= 3) {
        processedValue = `${processedValue.slice(0, 2)}/${processedValue.slice(
          2,
        )}`;
      }
      setExpiry(processedValue);
    } else if (name === "cvc") {
      processedValue = value.replace(/\D/g, "").slice(0, 4);
      setCvc(processedValue);
    }
  };

  const detectCardType = (cardNumber) => {
    const sanitizedNumber = cardNumber.replace(/\D/g, "");

    let cardType = "";

    if (/^4/.test(sanitizedNumber)) {
      cardType = "visa";
    } else if (
      /^5[1-5]/.test(sanitizedNumber) ||
      /^2[2-7]/.test(sanitizedNumber)
    ) {
      cardType = "mastercard";
    } else if (/^3[47]/.test(sanitizedNumber)) {
      cardType = "amex";
    } else if (/^6(?:011|5|4[4-9]|22)/.test(sanitizedNumber)) {
      cardType = "discover";
    } else if (/^35[2-8]/.test(sanitizedNumber)) {
      cardType = "jcb";
    }

    setCardType(cardType);
  };

  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case "cardNumber":
        cardValid = isValidCardNumber(value);
        errors.cardNumber = cardValid ? "" : "Invalid card number";
        break;
      case "cvc":
        cvcValid = isValidCVC(value);
        errors.cvc = cvcValid ? "" : "Invalid CVC";
        break;
      case "expiry":
        expiryValid = isValidExpiry(value);
        errors.expiry = expiryValid ? "" : "Invalid expiry date";
        break;
      default:
        break;
    }

    setErrors(errors);
    setCardValid(cardValid);
    setCvcValid(cvcValid);
    setExpiryValid(expiryValid);
  };

  const isValidCardNumber = (cardNumber) => {
    const sanitizedNumber = cardNumber.replace(/\D/g, "");
    let sum = 0;
    let shouldDouble = false;

    for (let i = sanitizedNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(sanitizedNumber.charAt(i), 10);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
  };

  const isValidCVC = (cvc) => {
    return /^\d{3,4}$/.test(cvc);
  };

  const isValidExpiry = (expiry) => {
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) return false;

    const [month, year] = expiry.split("/").map(Number);
    const currentYear = new Date().getFullYear() % 100; // 获取当前年份的后两位
    const currentMonth = new Date().getMonth() + 1; // 获取当前月份（0-11）

    return (
      year > currentYear || (year === currentYear && month >= currentMonth)
    );
  };

  const renderCardIcon = () => {
    switch (cardType) {
      case "visa":
        return <Visa size={30} />;
      case "mastercard":
        return <MasterCard size={30} />;
      case "amex":
        return <American size={30} />;
      case "discover":
        return <Discover size={30} />;
      case "jcb":
        return <JCB size={30} />;
      default:
        return (
          <div className="default-icons">
            <Visa size={24} />
            <MasterCard size={24} />
            <American size={24} />
            <Discover size={24} />
            <JCB size={24} />
          </div>
        );
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const getValid = () => {
    let payment = {
      cvc,
      cardNumber,
      expiry,
    };
    return payment;
  };

  const getPaymentVaild = () => {
    let paymentVaild = cvcValid && expiryValid && cardValid;
    return paymentVaild;
  };

  return (
    <React.Fragment>
      {" "}
      <div className="payment-container">
        <div className="card-title">Card Information</div>
        <div className="card-row">
          <div className="full">
            <div className="payment-input">
              <input
                type="text"
                name="cardNumber"
                placeholder="0000 0000 0000 0000"
                value={cardNumber}
                onChange={handleInputChange}
                maxLength="19" // 限制输入长度为19个字符（包括空格）
                onBlur={handleBlur}
                className={errors.cardNumber ? "input-error" : ""}
              />
              <div className="card-icon">{renderCardIcon()}</div>
            </div>
            {errors.cardNumber && (
              <div className="error-message">{errors.cardNumber}</div>
            )}
          </div>
        </div>
        <div className="card-row">
          <div className="half">
            <input
              type="text"
              name="expiry"
              placeholder="MM/YY"
              value={expiry}
              onChange={handleInputChange}
              maxLength="5" // 限制长度为5，包括斜杠
              onBlur={handleBlur}
              className={errors.expiry ? "input-error" : ""}
            />{" "}
            {errors.expiry && (
              <div className="error-message">{errors.expiry}</div>
            )}
          </div>
          <div className="half">
            <input
              type="text"
              name="cvc"
              placeholder="CVC"
              value={cvc}
              onChange={handleInputChange}
              maxLength="4" // 限制长度为4
              onBlur={handleBlur}
              className={errors.cvc ? "input-error" : ""}
            />{" "}
            {errors.cvc && <div className="error-message">{errors.cvc}</div>}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

export default Payment;
